import { every, filter, get, map, omit, orderBy, pickBy, size, some } from 'lodash';
import { scriptTypes } from 'constants/scripts';
import { UNIT_TYPES } from 'constants/unitTypes';

import {
  getAvgEstGrossRevenueForZones,
  getAvgEstYieldForZones,
  getAvgSeedRateForZones,
  getAvgSeedTargetYieldForZones,
  getNumSeedBagsForZones,
  getTotalAreaForZones,
  groupZonesBySeedProduct
} from 'util/zoneOperations';

// Selectors
import {
  getActiveFieldId,
  getActiveProductYear
} from 'current/selectors';
import {
  getBagSizeForScript,
  getCropForScript,
  getScriptsForFieldId,
  getScriptsForFieldIdAndProductYear,
  getScriptsSortedForManageScriptList,
  getScriptTypeForScript,
  isAdvancedScript,
  isCompleteScript,
  isPrimaryScript,
  scriptIsInProductYear
} from 'scripts/selectors';
import { getSeedApplicationsForZone } from 'scripts/zones/planting-selectors';

export const isPlantingScript = (script) => getScriptTypeForScript(script) === scriptTypes.PLANTING;
export const hasMissingSeedRates = (script) => {
  const { zones } = script;

  return some(zones, (zone) => {
    const seedApplication = getSeedApplicationsForZone(zone)[0];
    const rate = get(seedApplication, 'rate.q');
    return !Number.isFinite(rate);
  });
};
export const hasMissingSeedProducts = (script) => {
  const { zones } = script;
  const everyZoneHasSeedProduct = every(zones, (zone) => {
    const seedApplication = getSeedApplicationsForZone(zone)[0];
    return seedApplication && seedApplication.product;
  });
  const everyZoneHasNoSeedProduct = every(zones, (zone) => {
    const seedApplication = getSeedApplicationsForZone(zone)[0];
    return !seedApplication || !seedApplication.product;
  });
  return !everyZoneHasSeedProduct && !everyZoneHasNoSeedProduct;
};

export const getPlantingScriptsForFieldId = (state, fieldId) => {
  const fieldScripts = getScriptsForFieldId(state, fieldId);
  return pickBy(fieldScripts, (script) => isPlantingScript(script) && isCompleteScript(script));
};

export const getPlantingScriptsIdsForActiveField = (state) => {
  const activeFieldId = getActiveFieldId(state);
  const fieldScripts = getScriptsForFieldId(state, activeFieldId);
  const plantingScriptsForFieldId = Object.values(fieldScripts).filter(isPlantingScript);
  return plantingScriptsForFieldId.map(({ id }) => id);
};

export const getPlantingScriptsForManageScriptList = (state) => {
  // get all scripts for the active field.
  const fieldId = getActiveFieldId(state);
  let fieldScripts = getPlantingScriptsForFieldId(state, fieldId);
  // get only scripts for active product year
  fieldScripts = Object.values(fieldScripts).filter((script) => {
    return scriptIsInProductYear(script, getActiveProductYear(state));
  });
  return getScriptsSortedForManageScriptList(state, fieldScripts);
};

export const getManageSeedSummary = (script, { bagSize }) => {
  const { zones } = script;
  const crop = getCropForScript(script);
  let zonesByProduct = groupZonesBySeedProduct(zones);
  // if there is at least one actual product, don't include unassigned in the summary
  if (size(zonesByProduct) > 1) {
    zonesByProduct = omit(zonesByProduct, ['unassigned']);
  }

  return orderBy(map(zonesByProduct, (productZones, productName) => {
    const sampleZone = productZones[0];
    const seedApplication = getSeedApplicationsForZone(sampleZone)[0];
    const values = {
      productName,
      crop,
      product: get(seedApplication, 'product'),
      area: getTotalAreaForZones(productZones),
      avgRate: getAvgSeedRateForZones(productZones),
      avgTargetYield: getAvgSeedTargetYieldForZones(productZones),
      numSeedBags: getNumSeedBagsForZones({ zones: productZones, bagSize })
    };

    if (isAdvancedScript(script)) {
      values.avgEstYield = getAvgEstYieldForZones(productZones);
      values.avgEstGrossRevenue = getAvgEstGrossRevenueForZones(productZones);
    }

    return values;
  }), ['productName']);
};

export const getEditSeedSummary = (state, script) => {
  const seedSummaryByProduct = getManageSeedSummary(script, { bagSize: getBagSizeForScript(state, script) });
  const avgSeedRate = seedSummaryByProduct
  .reduce((total, { avgRate: { q: avgRate } }) => total + avgRate, 0) / seedSummaryByProduct.length;
  const totalNumSeedBags = seedSummaryByProduct
  .reduce((total, { numSeedBags: { q: numSeedBagsOfProduct } }) =>
    total + Math.ceil(numSeedBagsOfProduct), 0);

  return {
    avgRate: { q: avgSeedRate, u: UNIT_TYPES.seed_ac} ,
    numSeedBags: { q: totalNumSeedBags, u: UNIT_TYPES.bag}
  };
};

export const getPrimaryPlantingScriptByProductYear = (state, productYear) => {
  const scripts = getScriptsForFieldIdAndProductYear(state, getActiveFieldId(state), productYear);
  const plantingScripts = pickBy(scripts, (script) => isPlantingScript(script) && isCompleteScript(script));
  const primaryScripts = filter(plantingScripts, isPrimaryScript);
  return primaryScripts[0];
};

export const activeYearHasPrimaryPlantingScripts = (state) => {
  return Boolean(getPrimaryPlantingScriptByProductYear(state, getActiveProductYear(state)));
};
